import Image from 'next/image';
import React from 'react';
import { getDurationHoursFromMinutes } from '../../utils/getCurrentDayOpeningHours';

interface KeyDetailsProps {
  duration?: number;
  guests?: string;
  cancellation?: string;
  guide?: string;
  skipTickets?: string;
  pickup?: string;
  meeting?: string;
  confidence?: string;
  intro?: string;
}

const KeyDetails: React.FC<KeyDetailsProps> = ({
  duration,
  guests,
  cancellation,
  guide,
  skipTickets,
  pickup,
  meeting,
  confidence,
  intro
}) => {
  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="text-[22px] md:text-[24px] lg:text-[26px] font-bold text-[#101010] mb-2">
        Key Details
      </div>
      {/* Duration */}
      {duration && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/duration.svg"
              alt="duration"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Duration:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {getDurationHoursFromMinutes(duration)}
            </span>
          </div>
        </div>
      )}
      {/* Number of Guests */}
      {guests && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/guests.svg"
              alt="guests"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Group Size:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {guests}
            </span>
          </div>
        </div>
      )}
      {/* Free cancellation */}

      <div className="flex items-start gap-3 py-1">
        <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
          <Image
            src="/free-cancellation.svg"
            alt="cancellation"
            width={30}
            height={30}
            className="object-contain"
          />
        </div>
        <div className="flex-1">
          <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
            Free cancellation:{' '}
          </span>
          <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
            100% refund when cancelled at least 24 hours in advance
          </span>
        </div>
      </div>

      {/* Live Tour Guide */}
      {guide && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/live-tour-guide.svg"
              alt="live tour guide"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Live Tour Guide:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {guide}
            </span>
          </div>
        </div>
      )}
      {/* Skip-the-Line Tickets */}
      {skipTickets && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/ticket.svg"
              alt="skip the line"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Skip the Ticket Line:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {skipTickets}
            </span>
          </div>
        </div>
      )}
      {/* Pick-up and Drop-off */}
      {pickup && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/pickup.svg"
              alt="pickup"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Pick-up and Drop-off:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {pickup}
            </span>
          </div>
        </div>
      )}
      {/* Meeting Point */}
      {meeting && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/meeting-point.svg"
              alt="meeting point"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Meeting Point:{' '}
            </span>
            <span className="text-[#677083] text-[14px] md:text-[16px] font-normal align-middle">
              {meeting}
            </span>
          </div>
        </div>
      )}
      {/* Book with Confidence */}
      {confidence && (
        <div className="flex items-start gap-3 py-1">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0 pt-[2px]">
            <Image
              src="/star.svg"
              alt="confidence"
              width={30}
              height={30}
              className="object-contain"
            />
          </div>
          <div className="flex-1 mt-[3px]">
            <span className="text-[#101010] text-[16px] font-bold whitespace-nowrap align-middle">
              Book with Confidence{' '}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeyDetails;
